import { forwardRef } from "react";

// ── Palette (matches the approved reference design) ──
const NAVY = "#123c8c";
const NAVY_DARK = "#0d2f70";
const CREAM = "#fff9ef";
const PINK = "#e23b7a";

const DECO = "/toppers"; // same-origin public assets (safe for html2canvas)

const RANK = {
  1: { label: "1st", tint: "#fcf3d4", band: "#f7ead0", ribbon: "#e23b3b",
       coinFrom: "#ffe07a", coinTo: "#e0a21a", coinText: "#7a5600",
       msg: "Excellent Performance!", msgColor: "#d62828" },
  2: { label: "2nd", tint: "#e9f1fb", band: "#dbe8fa", ribbon: "#2456b0",
       coinFrom: "#eef1f6", coinTo: "#aeb6c4", coinText: "#374151",
       msg: "Great Achievement!", msgColor: "#1e5fbf" },
  3: { label: "3rd", tint: "#ebf6ec", band: "#dcefe0", ribbon: "#2e8b4e",
       coinFrom: "#f0c08a", coinTo: "#b87333", coinText: "#5a3410",
       msg: "Well Done! Keep It Up!", msgColor: "#2e8b4e" },
};

function initials(name) {
  const p = (name || "").trim().split(/\s+/).filter(Boolean);
  return ((p[0]?.[0] || "") + (p[1]?.[0] || "")).toUpperCase() || "?";
}

function Medal({ rank }) {
  const m = RANK[rank];
  return (
    <div style={{ width: 124, position: "relative", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <div style={{ position: "relative", width: 92, height: 92, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <img src={`${DECO}/laurel.png`} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }} />
        <div style={{
          width: 54, height: 54, borderRadius: "50%",
          background: `radial-gradient(circle at 35% 30%, ${m.coinFrom}, ${m.coinTo})`,
          border: "3px solid #ffffff", boxShadow: "0 3px 8px rgba(0,0,0,0.28)",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: m.coinText, fontFamily: "'Montserrat', sans-serif", fontWeight: 900,
          fontSize: 19, lineHeight: 1,
        }}>{m.label}</div>
      </div>
      <div style={{
        marginTop: -5, background: m.ribbon, color: "#fff", fontFamily: "'Montserrat', sans-serif",
        fontWeight: 800, fontSize: 11, letterSpacing: 2, padding: "3px 14px 4px", borderRadius: 4,
        boxShadow: `0 3px 7px ${m.ribbon}66`,
      }}>RANK</div>
    </div>
  );
}

function StudentCell({ s, rank, apiBase, first }) {
  const m = RANK[rank];
  const photoUrl = s.has_photo && s.student_id ? `${apiBase}/profile/photo/${s.student_id}` : null;
  const _cls = s.class_name != null ? String(s.class_name) : "";
  const _isPP = ["Play Group", "LKG", "UKG"].includes(_cls);
  const classLabel = _cls ? (_isPP ? _cls : `Class ${_cls}-${s.section || ""}`.replace(/-$/, "")) : "Class & Section";
  const pct = s.percentage != null && s.percentage !== "" ? (typeof s.percentage === "number" ? `${s.percentage}%` : s.percentage) : "";
  const metaLine = [classLabel, pct].filter(Boolean).join(" · ");
  return (
    <div style={{
      flex: 1, display: "flex", alignItems: "center", gap: 12, padding: "0 14px",
      borderLeft: first ? "none" : `1.5px solid ${m.band}`, minWidth: 0,
    }}>
      <div style={{ width: 74, height: 84, borderRadius: 10, overflow: "hidden", background: NAVY, border: "3px solid #ffffff", boxShadow: "0 3px 7px rgba(0,0,0,0.18)", flexShrink: 0 }}>
        {photoUrl ? (
          <img src={photoUrl} alt={s.name} crossOrigin="anonymous" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        ) : (
          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontSize: 26 }}>{initials(s.name)}</div>
        )}
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ color: NAVY, fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontSize: 16, lineHeight: 1.35, paddingBottom: 1 }}>
          {s.name || "Student Name"}
        </div>
        <div style={{ color: "#5b6a86", fontFamily: "'Montserrat', sans-serif", fontWeight: 600, fontSize: 12, marginTop: 4, lineHeight: 1.35 }}>
          {metaLine}
        </div>
        <div style={{ marginTop: 9, height: 1, background: m.band }} />
        <div style={{ color: m.msgColor, fontFamily: "'Montserrat', sans-serif", fontWeight: 700, fontSize: 12.5, marginTop: 9, lineHeight: 1.3 }}>
          {m.msg}
        </div>
      </div>
    </div>
  );
}

function RankRow({ r }) {
  const m = RANK[r.rank];
  const students = (r.students || []).slice(0, 3);
  return (
    <div data-testid={`topper-row-${r.rank}`} style={{
      background: m.tint, borderRadius: 16, border: `1.5px solid ${m.band}`,
      display: "flex", alignItems: "stretch", padding: "8px 16px", minHeight: 104,
      boxShadow: "0 2px 8px rgba(11,42,107,0.06)",
    }}>
      <Medal rank={r.rank} />
      <div style={{ flex: 1, display: "flex", alignItems: "center" }}>
        {students.map((s, i) => (
          <StudentCell key={s.student_id || s.name || i} s={s} rank={r.rank} apiBase={window.__API_BASE__} first={i === 0} />
        ))}
      </div>
    </div>
  );
}

function Star({ size = 14, color = PINK }) {
  return <span style={{ color, fontSize: size, lineHeight: 1 }}>★</span>;
}

const ClassToppersPoster = forwardRef(function ClassToppersPoster({ data, apiBase }, ref) {
  window.__API_BASE__ = apiBase;
  const { profile = {}, class_name, section, exam_short, exam_label, academic_year, ranks = [], note } = data || {};
  const visibleRanks = ranks.filter((r) => (r.students || []).length > 0);
  const _isPP = ["Play Group", "LKG", "UKG"].includes(class_name);
  const classLabel = class_name ? (_isPP ? class_name : `Class ${class_name}`) : "";
  const contextPill = [classLabel && `${classLabel}${section ? ` · Sec ${section}` : ""}`, (exam_label || exam_short), academic_year && `AY ${academic_year}`].filter(Boolean).join("   •   ");
  const today = new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });

  return (
    <div
      ref={ref}
      data-testid="class-toppers-poster"
      style={{ width: 1123, height: 794, background: CREAM, position: "relative", overflow: "hidden", display: "flex", flexDirection: "column", fontFamily: "'Montserrat', sans-serif" }}
    >
      <div style={{ position: "absolute", inset: 6, border: `2px solid ${NAVY}22`, borderRadius: 14, pointerEvents: "none", zIndex: 5 }} />

      {/* ── Header ── */}
      <div style={{ background: NAVY, padding: "12px 40px", display: "flex", alignItems: "center", gap: 20, position: "relative", zIndex: 3, flexShrink: 0 }}>
        {profile.logo ? (
          <div style={{ width: 76, height: 76, borderRadius: 10, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden" }}>
            <img src={profile.logo} alt="logo" crossOrigin="anonymous" style={{ width: "94%", height: "94%", objectFit: "contain" }} />
          </div>
        ) : null}
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: "'Merriweather', serif", fontWeight: 900, color: "#fff", fontSize: 38, lineHeight: 1.02 }}>
            {profile.name || "School Name"}
          </div>
          {profile.address ? <div style={{ color: "#dbe6ff", fontSize: 14, marginTop: 4 }}>{profile.address}</div> : null}
          {profile.phone ? <div style={{ color: "#dbe6ff", fontSize: 14, marginTop: 2 }}>Phone: {profile.phone}</div> : null}
        </div>
      </div>

      {/* ── Body ── */}
      <div style={{ flex: 1, minHeight: 0, position: "relative", padding: "8px 30px 0", display: "flex", flexDirection: "column" }}>
        {/* Bunting */}
        <img src={`${DECO}/bunting.png`} alt="" style={{ position: "absolute", top: 2, left: 8, width: 300, zIndex: 1 }} />
        <img src={`${DECO}/bunting.png`} alt="" style={{ position: "absolute", top: 2, right: 8, width: 300, transform: "scaleX(-1)", zIndex: 1 }} />
        {/* Corner decorations */}
        <img src={`${DECO}/trophy_books.png`} alt="" style={{ position: "absolute", top: 42, left: 20, width: 118, zIndex: 1 }} />
        <img src={`${DECO}/cap_books.png`} alt="" style={{ position: "absolute", top: 28, right: 22, width: 128, zIndex: 1 }} />

        {/* Congratulations block */}
        <div style={{ textAlign: "center", position: "relative", zIndex: 2, marginTop: 2, flexShrink: 0 }}>
          <div style={{ fontFamily: "'Dancing Script', cursive", color: PINK, fontSize: 28, fontWeight: 700, lineHeight: 1, paddingBottom: 12 }}>
            Congratulations
          </div>
          <div style={{ display: "inline-block", position: "relative" }}>
            <div style={{ background: NAVY, color: "#fff", fontFamily: "'Montserrat', sans-serif", fontWeight: 900, fontSize: 42, letterSpacing: 2, padding: "6px 50px 8px", borderRadius: 8, boxShadow: "0 8px 18px rgba(11,42,107,0.28)" }}>
              CLASS TOPPERS
            </div>
          </div>
          <div style={{ marginTop: 8, color: NAVY, fontFamily: "'Montserrat', sans-serif", fontWeight: 700, fontSize: 15 }}>
            <Star />&nbsp;&nbsp;We are proud of your hard work and dedication.&nbsp;&nbsp;<Star />
          </div>
          {contextPill ? (
            <div style={{ marginTop: 6 }}>
              <span style={{ display: "inline-block", background: "#eef2fb", color: NAVY, fontSize: 12, fontWeight: 700, padding: "3px 16px", borderRadius: 999 }}>{contextPill}</span>
            </div>
          ) : null}
        </div>

        {/* Rank rows */}
        <div style={{ position: "relative", zIndex: 2, flex: 1, minHeight: 0, display: "flex", flexDirection: "column", justifyContent: "center", gap: 10, marginTop: 8, paddingBottom: 4 }}>
          {visibleRanks.length === 0 ? (
            <div style={{ textAlign: "center", color: "#94a3b8", fontSize: 16 }}>No toppers to display yet.</div>
          ) : (
            visibleRanks.map((r) => <RankRow key={r.rank} r={r} />)
          )}
        </div>
        {/* bottom-right decoration */}
        <img src={`${DECO}/star_trophy_books.png`} alt="" style={{ position: "absolute", bottom: 4, right: 16, width: 96, zIndex: 1, opacity: 0.96 }} />
      </div>

      {/* ── Footer ── */}
      <div style={{ position: "relative", zIndex: 3, flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 40px 10px", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 150 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={NAVY} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
            <div style={{ lineHeight: 1.2 }}>
              <div style={{ color: "#5b6a86", fontSize: 11, fontWeight: 600 }}>Announcement Date:</div>
              <div style={{ color: NAVY, fontSize: 15, fontWeight: 800 }}>{today}</div>
            </div>
          </div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#fff", border: `2px solid ${NAVY}22`, borderRadius: 999, padding: "8px 22px", boxShadow: "0 2px 8px rgba(11,42,107,0.08)" }}>
            <Star color="#f4b400" />
            <span style={{ color: NAVY, fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontSize: 14, textAlign: "center", lineHeight: 1.2 }}>
              {note && String(note).trim() ? note : "Keep aiming high and inspiring others!"}
            </span>
            <Star color="#f4b400" />
          </div>
          <div style={{ textAlign: "right", minWidth: 150, lineHeight: 1.25 }}>
            <div style={{ color: "#5b6a86", fontSize: 12 }}>Regards,</div>
            <div style={{ color: NAVY, fontSize: 15, fontWeight: 800 }}>Principal</div>
            <div style={{ color: "#5b6a86", fontSize: 12 }}>{profile.name || "School"}</div>
          </div>
        </div>
        <div style={{ background: NAVY, color: "#cdddff", textAlign: "center", padding: "6px 0", fontSize: 11, fontWeight: 600, letterSpacing: 0.3 }}>
          © {profile.name || "School"} &nbsp;|&nbsp; Built by ZeroLabs Digitals
        </div>
      </div>
    </div>
  );
});

export default ClassToppersPoster;
